/**
 * DeveloperRegistrationPage - Developer Program Registration Page
 *
 * Centered form layout with simple white card on gray background
 * Uses React Hook Form + Zod validation, i18n for labels.
 */

import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { ECGridColors } from '@/types/colors';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Check, Loader2, X } from 'lucide-react';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import type { DeveloperRegistrationFormData } from './DeveloperRegistrationForm.hook';
import { useDeveloperRegistrationForm } from './DeveloperRegistrationForm.hook';
import { registrationSchema, type RegistrationFormValues } from './developer-registration.schema';
import { PROGRAM_TYPE, type ProgramType } from '@/application/queries/developerRegistration';

// ============================================================================
// Props Interface
// ============================================================================

export interface DeveloperRegistrationPageProps {
  antiforgeryToken?: string;
  programType?: ProgramType;
}

// ============================================================================
// Loading Spinner Component
// ============================================================================

const LoadingSpinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <Loader2 className={`animate-spin ${className}`} aria-hidden="true" />
);

// ============================================================================
// Form Input Component
// ============================================================================

interface FormInputProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormInputProps> = ({ id, label, required, error, hint, children }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-medium text-gray-900">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-gray-600 text-xs">{hint}</p>}
    {error && (
      <p className="text-destructive text-xs flex items-center gap-1" role="alert">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const DeveloperRegistrationPage = React.forwardRef<
  HTMLDivElement,
  DeveloperRegistrationPageProps
>((props, ref) => {
  const { antiforgeryToken = '', programType = PROGRAM_TYPE.DEVELOPER } = props;
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);
  const isDemo = programType === PROGRAM_TYPE.DEMO;

  // Custom hook for form logic
  const {
    accessCodeValidating,
    accessCodeResult,
    accessCodeError,
    isSubmitting,
    submitError,
    fieldErrors,
    validateAccessCode,
    submitForm,
    clearFieldError,
  } = useDeveloperRegistrationForm({ antiforgeryToken, programType });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      companyName: '',
      companyAddress: '',
      ownerContactName: '',
      ownerContactEmail: '',
      technicalContactName: '',
      technicalContactEmail: '',
      accessCode: '',
    },
  });

  // Watch access code for validation
  const accessCodeValue = watch('accessCode');

  const handleAccessCodeBlur = useCallback(() => {
    if (accessCodeValue) {
      validateAccessCode(accessCodeValue);
    }
  }, [accessCodeValue, validateAccessCode]);

  const onSubmit = useCallback(
    (data: RegistrationFormValues) => {
      const formData: DeveloperRegistrationFormData = {
        companyName: data.companyName,
        companyAddress: data.companyAddress,
        ownerContactName: data.ownerContactName,
        ownerContactEmail: data.ownerContactEmail,
        technicalContactName: data.technicalContactName || undefined,
        technicalContactEmail: data.technicalContactEmail || undefined,
        accessCode: data.accessCode || undefined,
      };
      submitForm(formData);
    },
    [submitForm, programType]
  );

  const getFieldError = (fieldName: keyof RegistrationFormValues): string | undefined => {
    return errors[fieldName]?.message || fieldErrors[fieldName];
  };

  const hasFieldError = (fieldName: keyof RegistrationFormValues): boolean => {
    return !!errors[fieldName] || !!fieldErrors[fieldName];
  };

  const inputBaseClass =
    'w-full px-3 py-2.5 border rounded-md text-sm bg-background text-foreground ' +
    'placeholder:text-muted-foreground/60 ' +
    'transition-colors duration-200 ' +
    'hover:border-gray-400 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed';

  const inputClassName = (hasError: boolean, hasSuccess?: boolean) =>
    `${inputBaseClass} ${
      hasError
        ? 'border-destructive focus-visible:ring-destructive'
        : hasSuccess
          ? 'border-green-500 focus-visible:ring-green-500'
          : 'border-input hover:border-primary/50'
    }`;

  return (
    <AnonymousPortalLayout ref={ref}>
      <div className="w-full max-w-lg space-y-6">
          {/* Form Card */}
          <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 sm:p-8">
            {/* Form Title */}
            <div className="text-center mb-6">
              
              {/* ECGrid Logo */}
              <div className="mb-4">
                <img
                  src="/img/ecgridlogo2x.png"
                  alt="ECGrid"
                  className="h-12 mx-auto"
                />
              </div>
              
              <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: ECGridColors.tealDark }}>
                {isDemo
                  ? t('DemoRegistration_FormTitle', 'Demo Program Registration')
                  : t('DeveloperRegistration_FormTitle', 'Register Account')}
              </h1>

              {/* Welcome Text - Compact styling */}
              <div
                className="text-sm leading-relaxed space-y-2.5 max-w-md mx-auto"
                style={{ color: ECGridColors.blue }}
              >
                <p>
                  {isDemo
                    ? t(
                        'DemoRegistration_Welcome',
                        'Thank you for your interest in the ECGrid Demo program. The Demo program lets you explore our platform with a limited sandbox environment.'
                      )
                    : t(
                        'DeveloperRegistration_Welcome',
                        'Thank you for your interest in the ECGrid Developer program. The Developer program allows you to learn about our solution in a hands-on way and begin integrating in a low-risk, low-cost environment.'
                      )}
                </p>
                <p>
                  {isDemo
                    ? t(
                        'DemoRegistration_Instructions',
                        'To get started, please fill out the fields below and click continue. Your demo account will be set up immediately.'
                      )
                    : t(
                        'DeveloperRegistration_Instructions',
                        'To sign up and get 30 days free, please fill out the fields below then click continue. You will be taken to a payment page where you can complete your registration and be provided with access to your program.'
                      )}
                </p>
              </div>
            </div>

            {/* Submit Error Banner */}
            {submitError && (
              <div className="mb-6">
                <div
                  className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span className="flex-1 text-sm">{submitError}</span>
                  <button
                    className="p-1 hover:bg-destructive/20 rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => clearFieldError('_form')}
                    type="button"
                    aria-label="Dismiss error message"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Company Section */}
              <div className="space-y-4">
                <FormField
                  id="companyName"
                  label={t('DeveloperRegistration_CompanyName', 'Company Name')}
                  required
                  error={getFieldError('companyName')}
                >
                  <input
                    {...register('companyName')}
                    id="companyName"
                    type="text"
                    autoComplete="organization"
                    placeholder={t(
                      'DeveloperRegistration_CompanyName_Placeholder',
                      'Acme Corporation'
                    )}
                    disabled={isSubmitting}
                    className={inputClassName(hasFieldError('companyName'))}
                  />
                </FormField>

                <FormField
                  id="companyAddress"
                  label={t('DeveloperRegistration_CompanyAddress', 'Company Address')}
                  required
                  error={getFieldError('companyAddress')}
                >
                  <textarea
                    {...register('companyAddress')}
                    id="companyAddress"
                    autoComplete="street-address"
                    placeholder={t(
                      'DeveloperRegistration_CompanyAddress_Placeholder',
                      '123 Business Ave, Suite 100\nCity, State 12345'
                    )}
                    rows={3}
                    disabled={isSubmitting}
                    className={`${inputClassName(hasFieldError('companyAddress'))} resize-none`}
                  />
                </FormField>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200"></div>

              {/* Contact Section */}
              <div className="space-y-4">
                <FormField
                  id="ownerContactName"
                  label={t('DeveloperRegistration_OwnerContactName', 'Owner Full Name')}
                  required
                  error={getFieldError('ownerContactName')}
                >
                  <input
                    {...register('ownerContactName')}
                    id="ownerContactName"
                    type="text"
                    autoComplete="name"
                    placeholder={t(
                      'DeveloperRegistration_OwnerContactName_Placeholder',
                      'John Smith'
                    )}
                    disabled={isSubmitting}
                    className={inputClassName(hasFieldError('ownerContactName'))}
                  />
                </FormField>

                <FormField
                  id="ownerContactEmail"
                  label={t('DeveloperRegistration_OwnerContactEmail', 'Owner Email Address')}
                  required
                  error={getFieldError('ownerContactEmail')}
                >
                  <input
                    {...register('ownerContactEmail')}
                    id="ownerContactEmail"
                    type="email"
                    autoComplete="email"
                    spellCheck="false"
                    placeholder={t(
                      'DeveloperRegistration_OwnerContactEmail_Placeholder',
                      'john@acme.com'
                    )}
                    disabled={isSubmitting}
                    className={inputClassName(hasFieldError('ownerContactEmail'))}
                  />
                </FormField>

                <FormField
                  id="technicalContactName"
                  label={t('DeveloperRegistration_TechnicalContactName', 'Technical Full Name')}
                  error={getFieldError('technicalContactName')}
                >
                  <input
                    {...register('technicalContactName')}
                    id="technicalContactName"
                    type="text"
                    autoComplete="name"
                    placeholder={t(
                      'DeveloperRegistration_TechnicalContactName_Placeholder',
                      'Jane Developer'
                    )}
                    disabled={isSubmitting}
                    className={inputClassName(hasFieldError('technicalContactName'))}
                  />
                </FormField>

                <FormField
                  id="technicalContactEmail"
                  label={t(
                    'DeveloperRegistration_TechnicalContactEmail',
                    'Technical Email Address'
                  )}
                  error={getFieldError('technicalContactEmail')}
                >
                  <input
                    {...register('technicalContactEmail')}
                    id="technicalContactEmail"
                    type="email"
                    autoComplete="email"
                    spellCheck="false"
                    placeholder={t(
                      'DeveloperRegistration_TechnicalContactEmail_Placeholder',
                      'dev@acme.com'
                    )}
                    disabled={isSubmitting}
                    className={inputClassName(hasFieldError('technicalContactEmail'))}
                  />
                </FormField>
              </div>

              {/* Referral Code Section - Hidden for Demo */}
              {!isDemo && (
                <>
              {/* Separator */}
              <div className="border-t border-gray-200"></div>

              <div className="space-y-4">
                <FormField
                  id="accessCode"
                  label={t('DeveloperRegistration_AccessCodeLabel', 'Referral Code')}
                  error={accessCodeError ?? undefined}
                  hint={t(
                    'DeveloperRegistration_AccessCodeHelp',
                    'Enter your code if you received one from a sales representative.'
                  )}
                >
                  <div className="relative">
                    <input
                      {...register('accessCode')}
                      id="accessCode"
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      placeholder={t('DeveloperRegistration_AccessCode_Placeholder', 'ABC123')}
                      disabled={isSubmitting}
                      onBlur={handleAccessCodeBlur}
                      className={`${inputClassName(!!accessCodeError, accessCodeResult?.isValid)} pr-10`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {accessCodeValidating && (
                        <LoadingSpinner className="h-4 w-4 text-muted-foreground" />
                      )}
                      {!accessCodeValidating && accessCodeResult?.isValid && (
                        <Check className="h-4 w-4 text-green-500" aria-hidden="true" />
                      )}
                      {!accessCodeValidating && accessCodeError && (
                        <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
                      )}
                    </span>
                  </div>
                  {accessCodeResult?.isValid && accessCodeResult?.salespersonEmail && (
                    <p
                      className="text-green-600 text-xs flex items-center gap-1 mt-1.5"
                      role="status"
                      aria-live="polite"
                    >
                      <Check className="h-3 w-3" />
                      {t('DeveloperRegistration_ValidCode_Referred', 'Referred by')}{' '}
                      {accessCodeResult.salespersonEmail}
                    </p>
                  )}
                  {accessCodeResult?.isValid &&
                    !accessCodeResult?.salespersonEmail &&
                    accessCodeValue && (
                      <p
                        className="text-green-600 text-xs flex items-center gap-1 mt-1.5"
                        role="status"
                        aria-live="polite"
                      >
                        <Check className="h-3 w-3" />
                        {t('DeveloperRegistration_ValidCode', 'Valid access code')}
                      </p>
                    )}
                </FormField>
              </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: isSubmitting ? '#9ca3af' : ECGridColors.blue,
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = ECGridColors.tealDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = ECGridColors.blue;
                  }
                }}
                className={`w-full py-3 px-4 text-white font-medium rounded-md
                    transition-colors duration-200
                    focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                    ${isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner className="h-5 w-5" />
                    {t('DeveloperRegistration_Submitting', 'Processing…')}
                  </span>
                ) : (
                  isDemo
                    ? t('DemoRegistration_Submit', 'Create Demo Account')
                    : t('DeveloperRegistration_Submit', 'Continue to Payment')
                )}
              </button>
            </form>
          </div>

      </div>
    </AnonymousPortalLayout>
  );
});

DeveloperRegistrationPage.displayName = 'DeveloperRegistrationPage';
